/* ============================================================
   SAM-546 — Sessions editor.

   Jira SAM-198 item "EDITOR DE SESIONES: grilla semanal, cupo e instructor
   por sesión, excepciones y feriados. Front sobre la API de horarios
   recurrentes del booking service."

   This section is a purpose-built, class/session-oriented VIEW over the
   exact same data the "Scheduling & Capacity" section already edits
   (`formData.operating_hours[day]` — open/close + `extra_windows`, and
   `formData.closures`). It does not introduce a new wire shape: every
   session shown here is one window (the day's base open/close, or one entry
   of `extra_windows`) as already defined by SAM-198
   (`functions/booking/operating_windows.py` on the lambda side,
   `src/utils/operatingWindows.ts` / `useServiceEditor.ts` here).

   Why a separate section instead of extending "Scheduling & Capacity":
   several agents are working on this page concurrently (SAM-546 coordination
   note) and that section's body is explicitly out of scope for this piece
   of work. It also reads better on its own — "Scheduling & Capacity" is the
   raw per-day open/close table; "Sessions" is the class-scheduling-shaped
   view of the same data (a weekly grid, one card per session) plus the
   exceptions/holidays list.

   Label-only fields (`label`, `capacity`, `instructor` on each session) —
   see `TimeWindow`'s doc comment in `src/utils/operatingWindows.ts` for the
   full, evidence-based explanation of why these have no reader today:
     - Capacity is uniform per service (`settings.intervals.max_per_slot`,
       SAM-198's own decision) and the booking engine's
       `RecurringScheduleScheduleBlock` schema
       (sam-booking-service `src/forms/RecurringScheduleForm.ts`,
       `PostRecurringScheduleSBlockSchema`) has no per-block capacity field
       to forward one to.
     - Instructor has no reader anywhere in this Lambda or the engine.
   Both are still collected here (free text) so an admin can document what a
   session actually is — "Yoga — Ana — 12" — without inventing a payload
   shape a future ticket would have to migrate away from.

   Shown only when `formData.booking_type === 'fixed'` (gated in
   ServiceEditorPage's `useSections`) — a 'day' service has no time-of-day
   sessions to schedule.
   ============================================================ */
import React from 'react';
import { Clock, Plus, Trash2, Users, UserRound, CalendarOff, Tag } from 'lucide-react';
import { useServiceEditor, DAYS_OF_WEEK, type OperatingHourForm } from '../../../hooks/useServiceEditor';
import { ServiceClosuresEditor } from '../../../components/ServiceClosuresEditor';
import type { TimeWindow } from '../../../utils/operatingWindows';
import { Notice } from '../../../components/ui/Notice';

type Editor = ReturnType<typeof useServiceEditor>;

function SectionCard({
  title, icon, caption, children,
}: { title: string; icon: React.ReactNode; caption?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section
      className="rounded-12 mb-4 bg-white"
      style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-12)' }}
    >
      <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--line-subtle)' }}>
        <div className="flex items-center gap-2">
          <span style={{ color: 'var(--tenant-primary, #111827)' }}>{icon}</span>
          <h2 className="text-[13px] font-semibold" style={{ color: 'var(--ink-900)' }}>{title}</h2>
        </div>
        {caption && (
          <p className="text-[11px] mt-1" style={{ color: 'var(--ink-400)' }}>{caption}</p>
        )}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

// One row of the grid = one session (a window). Identifies itself either as
// the day's base window (index === null) or one entry of extra_windows.
interface SessionRow {
  day: string;
  index: number | null; // null = base open/close window
  window: TimeWindow;
}

function sessionsForDay(day: string, config: OperatingHourForm | undefined): SessionRow[] {
  if (!config?.enabled) return [];
  const rows: SessionRow[] = [];
  if (config.open && config.close) {
    rows.push({
      day, index: null,
      window: { open: config.open, close: config.close, label: config.label, capacity: config.capacity, instructor: config.instructor },
    });
  }
  config.extra_windows.forEach((w, i) => rows.push({ day, index: i, window: w }));
  return rows.sort((a, b) => a.window.open.localeCompare(b.window.open));
}

const SessionCard: React.FC<{
  row: SessionRow;
  onChange: (field: 'open' | 'close' | 'label' | 'capacity' | 'instructor', value: string) => void;
  onRemove: () => void;
  canRemove: boolean;
}> = ({ row, onChange, onRemove, canRemove }) => {
  const { window: w } = row;
  return (
    <div
      className="rounded-8 mb-2 p-2"
      style={{ border: '1px solid var(--line-subtle)', borderRadius: 'var(--r-8, 8px)', background: 'var(--surface-2, #F9FAFB)' }}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <Clock size={12} style={{ color: 'var(--ink-400)' }} />
        <input
          type="time"
          value={w.open}
          onChange={(e) => onChange('open', e.target.value)}
          className="rounded px-1.5 py-1 text-[12px]"
          style={{ border: '1px solid var(--line)', width: 92 }}
        />
        <span className="text-[11px]" style={{ color: 'var(--ink-400)' }}>–</span>
        <input
          type="time"
          value={w.close}
          onChange={(e) => onChange('close', e.target.value)}
          className="rounded px-1.5 py-1 text-[12px]"
          style={{ border: '1px solid var(--line)', width: 92 }}
        />
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            title="Remove session"
            className="ml-auto rounded p-1"
            style={{ color: 'var(--danger, #B91C1C)' }}
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>

      <input
        type="text"
        value={w.label || ''}
        onChange={(e) => onChange('label', e.target.value)}
        placeholder="Session name (e.g. Yoga, Kids swim)"
        className="w-full rounded px-1.5 py-1 text-[12px] mb-1.5"
        style={{ border: '1px solid var(--line)' }}
      />

      <div className="flex items-center gap-1.5">
        <div className="flex items-center gap-1 flex-1">
          <Users size={11} style={{ color: 'var(--ink-400)' }} />
          <input
            type="text"
            value={w.capacity || ''}
            onChange={(e) => onChange('capacity', e.target.value)}
            placeholder="Cupo"
            title="Advisory only — actual capacity is Max Per Slot in Scheduling & Capacity (uniform per service)"
            className="w-full rounded px-1.5 py-1 text-[12px]"
            style={{ border: '1px solid var(--line)' }}
          />
        </div>
        <div className="flex items-center gap-1 flex-1">
          <UserRound size={11} style={{ color: 'var(--ink-400)' }} />
          <input
            type="text"
            value={w.instructor || ''}
            onChange={(e) => onChange('instructor', e.target.value)}
            placeholder="Instructor"
            className="w-full rounded px-1.5 py-1 text-[12px]"
            style={{ border: '1px solid var(--line)' }}
          />
        </div>
      </div>
    </div>
  );
};

export function SessionsSection({ editor }: { editor: Editor }) {
  const {
    formData, updateOperatingHour, addExtraWindow, removeExtraWindow, updateExtraWindow,
    addClosure, removeClosure, updateClosure,
  } = editor;
  return (
    <>
      <SectionCard
        title="Weekly sessions"
        icon={<Tag size={14} />}
        caption={
          <>
            One card per bookable session (Mon–Sun). Session name and instructor are labels only —
            nothing reads them yet. Capacity is advisory here too: the number every session actually
            sells against is <strong>Max Per Slot</strong> in "Scheduling &amp; Capacity" (uniform
            across every session of this service — the booking engine has no per-session capacity
            field). To add a day, enable it first in "Scheduling &amp; Capacity".
          </>
        }
      >
        {formData.hours_source === 'business' && (
          <Notice variant="warn" className="mb-3">
            This service follows the business’s hours (Scheduling &amp; Capacity → Operating hours).
            The sessions below are kept but not used until you switch back to Custom hours.
          </Notice>
        )}
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}
        >
          {DAYS_OF_WEEK.map(({ key, label }) => {
            const config = formData.operating_hours[key];
            const rows = sessionsForDay(key, config);
            const dayEnabled = !!config?.enabled;
            return (
              <div key={key} className="min-w-0">
                <div
                  className="text-[11px] font-semibold uppercase mb-1.5 flex items-center justify-between"
                  style={{ color: dayEnabled ? 'var(--ink-700)' : 'var(--ink-300, #CBD5E1)' }}
                >
                  <span>{label}</span>
                  {!dayEnabled && <span className="text-[10px] font-normal normal-case">closed</span>}
                </div>

                {dayEnabled ? (
                  <>
                    {rows.length === 0 && (
                      <p className="text-[11px] mb-2" style={{ color: 'var(--ink-400)' }}>
                        No time set — open/close is blank in Scheduling &amp; Capacity.
                      </p>
                    )}
                    {rows.map((row) => (
                      <SessionCard
                        key={row.index === null ? 'base' : `extra-${row.index}`}
                        row={row}
                        canRemove={row.index !== null}
                        onChange={(field, value) => {
                          if (row.index === null) {
                            updateOperatingHour(key, field, value);
                          } else {
                            updateExtraWindow(key, row.index, field, value);
                          }
                        }}
                        onRemove={() => {
                          if (row.index !== null) removeExtraWindow(key, row.index);
                        }}
                      />
                    ))}
                    <button
                      type="button"
                      onClick={() => addExtraWindow(key)}
                      className="w-full flex items-center justify-center gap-1 rounded py-1.5 text-[11px]"
                      style={{ border: '1px dashed var(--line)', color: 'var(--ink-500)' }}
                    >
                      <Plus size={11} /> Add session
                    </button>
                  </>
                ) : (
                  <p className="text-[11px]" style={{ color: 'var(--ink-300, #CBD5E1)' }}>—</p>
                )}
              </div>
            );
          })}
        </div>
      </SectionCard>

      <SectionCard
        title="Exceptions & holidays"
        icon={<CalendarOff size={14} />}
        caption="Full-day closures for this service — a holiday, a maintenance day, an instructor out sick. Only closed days are supported today: the booking engine's recurring schedule has no per-date 'altered hours' override, so a day with different hours needs its own note here plus a manual change to that day's Scheduling & Capacity window for the affected date range."
      >
        <ServiceClosuresEditor
          closures={formData.closures}
          onAdd={addClosure}
          onRemove={removeClosure}
          onChange={updateClosure}
        />
      </SectionCard>
    </>
  );
}
